// frontend/src/components/ExportMenu.jsx
//
// v2.5.153 — small dropdown button used by table views to export the
// current rows as CSV / Excel / PDF. Pass a `getRows` callback that
// returns the current rows (so filters apply at click-time), columns,
// title, and filename stem. The component owns the menu open/close
// state and click-outside dismissal; the parent just provides data.

import { useEffect, useRef, useState } from 'react';
import { Download, ChevronDown, FileText, FileSpreadsheet } from 'lucide-react';
import { exportTable } from '../utils/tableExport.js';

export default function ExportMenu({ filename, title, columns, getRows, label = 'Export' }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  const fire = async (format) => {
    setOpen(false);
    setBusy(true);
    try {
      const rows = (typeof getRows === 'function') ? getRows() : [];
      await exportTable({ format, filename, title, columns, rows });
    } catch (err) {
      console.error('export failed:', err);
      // eslint-disable-next-line no-alert
      alert('Export failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={busy}
        className="text-xs font-semibold px-3 py-2 rounded-lg"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'var(--surface-2)', color: 'var(--text)',
          border: '1px solid var(--border)',
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}>
        <Download size={13} />
        {busy ? 'Exporting…' : label}
        <ChevronDown size={12} style={{ opacity: 0.6 }} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            minWidth: 160, zIndex: 50,
            background: 'var(--surface-1)', border: '1px solid var(--border)',
            borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
            padding: 4,
          }}>
          {[
            { fmt: 'csv',  Icon: FileText,        label: 'CSV' },
            { fmt: 'xlsx', Icon: FileSpreadsheet, label: 'Excel (.xlsx)' },
            { fmt: 'pdf',  Icon: FileText,        label: 'PDF' },
          ].map(({ fmt, Icon, label }) => (
            <button
              key={fmt}
              type="button"
              onClick={() => fire(fmt)}
              role="menuitem"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', textAlign: 'left', padding: '8px 10px',
                background: 'transparent', border: 'none', color: 'var(--text)',
                cursor: 'pointer', fontSize: 13, borderRadius: 6,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <Icon size={14} style={{ opacity: 0.75 }} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
